import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Caption1Strong, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { DocumentTextFilled, ShieldCheckmarkFilled, type FluentIcon } from '@fluentui/react-icons';
import { AccessType } from '@modules/access/types';
import SignInContext from '@modules/users/contexts/SignInContext.context';
import { EShortcutDestinationType, type TOrganizationShortcuts } from '@modules/users/types/shortcuts.types';
import { TERMINOLOGY } from '@shared/i18n/terminology';

const SHORTCUTS: { key: keyof TOrganizationShortcuts; label: string; Icon: FluentIcon }[] = [
  { key: 'TD', label: TERMINOLOGY.technicalDocumentation.upper, Icon: DocumentTextFilled },
  { key: 'QMS', label: TERMINOLOGY.qualityManagement.upper, Icon: ShieldCheckmarkFilled },
];

type TShortcutLink = { key: keyof TOrganizationShortcuts; label: string; Icon: FluentIcon; href?: string };

export const useShortcutLinks = (): TShortcutLink[] => {
  const { organization, folderAccess } = useContext(SignInContext);

  return SHORTCUTS.flatMap(({ key, label, Icon }) => {
    const shortcut = organization.shortcuts?.[key];
    if (!shortcut) return [];
    if (shortcut.destinationType === EShortcutDestinationType.NONE) return [{ key, label, Icon }];
    if (shortcut.destinationType === EShortcutDestinationType.ALL_PRODUCTS)
      return [{ key, label, Icon, href: '/products' }];
    const accessRight = folderAccess.shortcuts[key];
    if (accessRight === undefined || accessRight === AccessType.NONE) return [];
    return [{ key, label, Icon, href: `/folder/${shortcut.destination}` }];
  });
};

const useStyles = makeStyles({
  rail: {
    flexShrink: 0,
    width: '112px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
    boxSizing: 'border-box',
  },
  link: {
    width: '100px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalSNudge,
    padding: `${tokens.spacingVerticalMNudge} ${tokens.spacingHorizontalXS} ${tokens.spacingVerticalM}`,
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusXLarge,
    color: tokens.colorNeutralForeground1,
    '&:hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    '&:hover .shortcut-tile': {
      boxShadow: tokens.shadow8,
    },
    '&:active': {
      backgroundColor: tokens.colorSubtleBackgroundPressed,
    },
  },
  tile: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    color: tokens.colorBrandForeground1,
    fontSize: '22px',
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationFaster,
  },
});

const ShortcutsSidebar = () => {
  const links = useShortcutLinks();
  const classes = useStyles();

  if (links.length === 0) return null;

  return (
    <nav className={classes.rail} aria-label="Shortcuts" data-shortcuts-rail>
      {links.map(({ key, label, Icon, href }) => {
        const content = (
          <>
            <span className={mergeClasses(classes.tile, 'shortcut-tile')}>
              <Icon />
            </span>
            <Caption1Strong align="center" className="w-full line-clamp-2">
              {label}
            </Caption1Strong>
          </>
        );
        return href ? (
          <Link key={key} to={href} className={classes.link}>
            {content}
          </Link>
        ) : (
          <div key={key} className={classes.link}>
            {content}
          </div>
        );
      })}
    </nav>
  );
};

export default ShortcutsSidebar;
